import React, { useState, useEffect } from 'react';
import { User, UserX, Loader, AlertCircle } from 'lucide-react';
import { getFriendsList, unfriend } from '../lib/friends';
import { useNavigate } from 'react-router-dom';

const Friends = () => {
  const navigate = useNavigate();
  const [friends, setFriends] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unfriending, setUnfriending] = useState<string | null>(null);

  const loadFriends = async () => {
    try {
      setLoading(true);
      const friendsList = await getFriendsList();
      setFriends(friendsList);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load friends');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFriends();
  }, []);

  const handleUnfriend = async (e: React.MouseEvent, friendshipId: string) => {
    e.stopPropagation(); // Prevent navigation when clicking the unfriend button
    try {
      setUnfriending(friendshipId);
      await unfriend(friendshipId);
      setFriends(prev => prev.filter(f => f.id !== friendshipId));
    } catch (err) {
      console.error('Error unfriending:', err);
      setError(err instanceof Error ? err.message : 'Failed to unfriend user');
    } finally {
      setUnfriending(null);
    }
  };

  const navigateToProfile = (friendId: string) => {
    navigate(`/profile/${friendId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
        <AlertCircle className="h-5 w-5 mr-2" />
        <span>{error}</span>
      </div>
    );
  }

  if (friends.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <User className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <p>No friends yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {friends.map((friendship) => (
        <div 
          key={friendship.id} 
          onClick={() => navigateToProfile(friendship.friend.id)}
          className="flex items-center justify-between bg-white p-4 rounded-lg shadow-sm hover:shadow transition cursor-pointer"
        >
          <div className="flex items-center space-x-3">
            {friendship.friend.profile_picture ? (
              <img
                src={friendship.friend.profile_picture}
                alt={friendship.friend.full_name}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                <User className="h-5 w-5 text-gray-400" />
              </div>
            )}
            <div>
              <h3 className="font-medium text-gray-900">{friendship.friend.full_name}</h3>
            </div>
          </div>
          <button
            onClick={(e) => handleUnfriend(e, friendship.id)}
            disabled={unfriending === friendship.id}
            className={`text-gray-400 hover:text-red-500 transition ${
              unfriending === friendship.id ? 'cursor-not-allowed opacity-50' : ''
            }`}
            title="Unfriend"
          >
            {unfriending === friendship.id ? (
              <Loader className="h-5 w-5 animate-spin" />
            ) : (
              <UserX className="h-5 w-5" />
            )}
          </button>
        </div>
      ))}
    </div>
  );
};

export default Friends;